import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, logout as apiLogout } from "../lib/api";
import { LanguageMenu } from "../components/LanguageMenu";
import { MapView } from "../components/map/MapView";
import { SakuraCoinIcon, StreakFireIcon } from "../components/animation/AnimatedUIIcon";
import { PrologueVideoModal } from "../components/animation/PrologueVideoModal";
import { Animal3DWidget } from "../components/3d/Animal3DWidget";
import { ReviewParcelModal } from "../components/review/ReviewParcelModal";
import type { AreaSummary, Task } from "../content/lesson";
import { MAP_AREAS, getAreaName, type AreaId, type MapArea } from "../content/map";

type Profile = { name: string; unlocked_areas: string[]; streak_days?: number; coin_balance?: number };
const UNITS_PER_SECTION = 5;

// マップ画面（ログイン後のメイン画面）。
// どうぶつたちの島を歩きながら生活日本語を身につけるCozyなゲーム世界観。
export function MapPage() {
  const { t, i18n } = useTranslation(["common", "learner"]);
  const navigate = useNavigate();
  const isJa = i18n.language === "ja";
  const [showPrologue, setShowPrologue] = useState(false);
  const [showReview, setShowReview] = useState(false);

  const hour = new Date().getHours();
  const timeGreeting = useMemo(() => {
    if (hour >= 5 && hour < 12) {
      return isJa ? "おはようさん！" : "Good morning!";
    } else if (hour >= 12 && hour < 18) {
      return isJa ? "まいど、こんにちは！" : "Hello!";
    } else {
      return isJa ? "おつかれさま、こんばんは！" : "Good evening!";
    }
  }, [hour, isJa]);

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api<Profile>("/api/learner/profile"),
  });
  const { data: areas = [] } = useQuery({
    queryKey: ["areas"],
    queryFn: () => api<AreaSummary[]>("/api/learner/areas"),
  });
  const { data: reviewData } = useQuery({
    queryKey: ["reviewTasks"],
    queryFn: () => api<{ tasks: Task[]; count: number }>("/api/learner/review"),
  });

  const unlocked = resolveUnlocked(profile?.unlocked_areas);
  const currentAreaId = resolveCurrentArea(unlocked, areas);
  const currentArea = MAP_AREAS.find((a) => a.id === currentAreaId);
  const progressByArea = Object.fromEntries(
    areas.map((a) => [a.id, { done: a.unit_done, total: a.unit_total, cleared: a.cleared }]),
  );

  function handleAreaTap(area: MapArea) {
    if (!unlocked.has(area.id)) return;
    navigate(`/area/${area.id}`);
  }

  function handleCourseTap(areaId: AreaId, courseIndex: number) {
    if (!unlocked.has(areaId)) return;
    const sectionIndex = Math.floor(courseIndex / UNITS_PER_SECTION) + 1;
    const unitIndex = (courseIndex % UNITS_PER_SECTION) + 1;
    navigate(`/unit/${areaId}-${sectionIndex}/${unitIndex}`);
  }

  return (
    <div
      className="relative w-full h-screen overflow-hidden"
      style={{ background: "linear-gradient(180deg,#fff7e6 0%,#dcece8 100%)" }}
    >
      <MapView
        onAreaTap={handleAreaTap}
        onCourseTap={handleCourseTap}
        isUnlocked={(a) => unlocked.has(a.id)}
        currentAreaId={currentAreaId}
        progressByArea={progressByArea}
        areas={areas}
      />

      <header className="absolute top-0 left-0 right-0 z-30 flex flex-wrap sm:flex-nowrap items-start justify-between gap-2 p-3 sm:p-4 pointer-events-none">
        {/* うさぎ先生の吹き出しUI（スマホ幅ではボタン列の下の段に全幅で置き、縦に潰れないようにする） */}
        <div className="order-last sm:order-none w-full sm:w-auto sm:max-w-[48vw] flex items-start gap-2.5 pointer-events-auto">
          {/* うさぎ先生アバター */}
          <div className="w-11 h-11 rounded-full border-2 border-[#d9b45f] bg-[#fffdf7] flex items-center justify-center text-xl shadow-md shrink-0 select-none">
            🐰
          </div>
          {/* 吹き出し本体 */}
          <div className="relative min-w-0 bg-[#fffdf7]/95 rounded-2xl shadow-lg px-4 py-2.5 border border-[#d9b45f]/30 flex flex-col gap-0.5">
            {/* 吹き出しのしっぽ */}
            <div className="absolute left-[-5px] top-[14px] w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-r-[5px] border-r-[#fffdf7]/95" />
            <div className="text-[10px] sm:text-xs text-[#8f332c] font-black tracking-wider uppercase leading-none flex items-center gap-1.5">
              <span>{isJa ? "うさぎ先生" : "Bunny Sensei"}</span>
              <span>•</span>
              <span>{timeGreeting}</span>
            </div>
            <div className="text-xs sm:text-sm text-[#273f7a] font-bold leading-snug">
              {isJa ? (
                <>次は <span className="text-[#8f332c] font-black">{currentArea?.nameJa}</span> やで！{currentArea?.residentEmoji}🐾</>
              ) : (
                <>Next is <span className="text-[#8f332c] font-black">{currentArea ? getAreaName(currentArea, i18n.language) : ""}</span>! {currentArea?.residentEmoji}🐾</>
              )}
            </div>
            <div className="text-[11px] sm:text-xs text-stone-600 font-medium leading-normal italic">
              「{isJa ? currentArea?.guideCopyJa : currentArea?.guideCopyEn}」
            </div>
          </div>
        </div>

        {/* ヘッダー右側: 動くアイコンステータスバー ＋ メニュー */}
        <div className="ml-auto flex items-center gap-2 pointer-events-auto">
          {/* 復習便ボタン（苦手タスクがある場合のみ表示） */}
          {reviewData && reviewData.count > 0 && (
            <button
              type="button"
              onClick={() => setShowReview(true)}
              title={isJa ? "島に届いた復習便" : "Island Review Parcel"}
              className="flex items-center gap-1.5 bg-gradient-to-r from-amber-100 via-amber-200/90 to-orange-100 rounded-xl shadow px-2.5 sm:px-3 h-11 text-xs font-black text-[#8f332c] border border-amber-400/60 hover:from-amber-200 hover:to-amber-300 transition active:scale-95 animate-pulse"
            >
              <span className="text-base">📦</span>
              <span className="hidden sm:inline">{isJa ? "復習便" : "Review"}</span>
              <span className="bg-[#8f332c] text-white rounded-full px-1.5 py-0.2 text-[10px]">
                {reviewData.count}
              </span>
            </button>
          )}

          {/* プロローグ動画ボタン */}
          <button
            onClick={() => setShowPrologue(true)}
            title={isJa ? "島のプロローグ動画を見る" : "Watch Island Prologue"}
            className="hidden sm:flex items-center gap-1.5 bg-[#fffdf7]/95 rounded-xl shadow px-3 h-11 text-xs font-black text-[#8f332c] border border-[#d9b45f]/30 hover:bg-[#fff9ea] transition active:scale-95"
          >
            <span>🎬</span>
            <span>{isJa ? "プロローグ" : "Prologue"}</span>
          </button>

          {/* ストリーク表示（動く炎アイコン） */}
          <div className="flex items-center gap-1 bg-[#fffdf7]/95 rounded-xl shadow px-2.5 h-11 border border-[#d9b45f]/30 text-[#8f332c] font-black text-xs">
            <StreakFireIcon size={20} />
            <span>{profile?.streak_days ?? 0}</span>
          </div>

          {/* コイン表示（動く3D回転桜コイン） */}
          <div className="flex items-center gap-1 bg-[#fffdf7]/95 rounded-xl shadow px-2.5 h-11 border border-[#d9b45f]/30 text-[#8f332c] font-black text-xs">
            <SakuraCoinIcon size={20} />
            <span>{profile?.coin_balance ?? 0}</span>
          </div>

          {/* タイピング練習（PC のみ。768px 未満では出さない：spec 11章⑧） */}
          <Link
            to="/typing"
            data-testid="nav-typing"
            aria-label={t("learner:typing_nav")}
            title={t("learner:typing_nav")}
            className="hidden md:flex items-center gap-1.5 bg-[#fffdf7]/95 rounded-xl shadow px-3 h-11 text-xs font-black text-[#273f7a] border border-[#d9b45f]/30 hover:bg-[#fff9ea] transition"
          >
            <KeyboardIcon />
            <span>{t("learner:typing_nav")}</span>
          </Link>
          <Link
            to="/room"
            aria-label={t("learner:my_room")}
            className="bg-[#fffdf7]/92 rounded-xl shadow w-11 h-11 flex items-center justify-center text-[#273f7a] border border-[#d9b45f]/30 hover:bg-[#fff9ea] transition"
          >
            <HomeIcon />
          </Link>
          <Link
            to="/leaderboard"
            aria-label={t("learner:leaderboard")}
            className="bg-[#fffdf7]/92 rounded-xl shadow w-11 h-11 flex items-center justify-center text-[#d9a441] border border-[#d9b45f]/30 hover:bg-[#fff9ea] transition"
          >
            <TrophyIcon />
          </Link>
          <LanguageMenu variant="compact" />
          <button
            onClick={apiLogout}
            aria-label={t("common:logout")}
            className="bg-[#fffdf7]/92 rounded-xl shadow w-11 h-11 flex items-center justify-center text-[#8f332c] border border-[#d9b45f]/30 hover:bg-[#fff9ea] transition"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
        </div>
      </header>

      {/* 復習便モーダル */}
      <ReviewParcelModal
        isOpen={showReview}
        onClose={() => setShowReview(false)}
        tasks={reviewData?.tasks ?? []}
      />

      {/* プロローグ動画モーダル */}
      <PrologueVideoModal
        isOpen={showPrologue}
        onClose={() => setShowPrologue(false)}
        isJa={isJa}
      />

      {/* 3Dどうぶつインタラクティブウィジェット */}
      <Animal3DWidget />
    </div>
  );
}

function resolveUnlocked(raw?: string[]): Set<string> {
  const ids = raw && raw.length > 0 ? raw : ["airport", "home"];
  const out = new Set<string>();
  for (const id of ids) {
    if (id === "self_room") {
      out.add("airport");
      out.add("home");
    } else {
      out.add(id);
    }
  }
  return out;
}

function resolveCurrentArea(unlocked: Set<string>, areas: AreaSummary[]): AreaId {
  const firstOpen = areas
    .filter((area) => area.unlocked && !area.cleared)
    .sort((a, b) => a.unlock_order - b.unlock_order)[0];
  if (firstOpen) return firstOpen.id;

  const current = MAP_AREAS
    .filter((area) => unlocked.has(area.id))
    .sort((a, b) => b.unlockOrder - a.unlockOrder)[0];
  return current?.id ?? "airport";
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l9-8 9 8M5 9.5V21h14V9.5M9 21v-6h6v6" />
    </svg>
  );
}

function KeyboardIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M7 4h10v2h3a1 1 0 0 1 1 1v3a4 4 0 0 1-4 4h-.34A6 6 0 0 1 13 17.92V20h3v2H8v-2h3v-2.08A6 6 0 0 1 7.34 14H7a4 4 0 0 1-4-4V7a1 1 0 0 1 1-1h3V4zm0 4H5v2a2 2 0 0 0 2 2V8zm10 0v4a2 2 0 0 0 2-2V8h-2z" />
    </svg>
  );
}
